import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import { SkeletonCard, SkeletonTable } from '../components/common/Skeleton';
import { 
  Wallet, 
  Cpu, 
  TrendingUp, 
  Users, 
  ArrowUpRight, 
  Plus, 
  Copy, 
  Check, 
  Gift, 
  ArrowDownRight, 
  Clock, 
  ShieldCheck,
  ChevronRight,
  ExternalLink
} from 'lucide-react';

const UserDashboard = ({ user, setActiveTab }) => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [mt5Accounts, setMt5Accounts] = useState([]);
  const [refSummary, setRefSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchDashboardData = async () => {
    try {
      const [resWallet, resMt5, resRef] = await Promise.all([
        axiosClient.get('/wallet/my-wallet'),
        axiosClient.get('/mt5/my-accounts'),
        axiosClient.get('/referral/my-tree')
      ]);

      setWallet(resWallet.data.wallet || null);
      setTransactions((resWallet.data.transactions || []).slice(0, 5));
      setMt5Accounts(resMt5.data.accounts || []);
      setRefSummary(resRef.data || null);
    } catch (err) {
      console.error('[User Dashboard Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleCopyLink = () => {
    const link = refSummary?.referralLink || `${window.location.origin}/?ref=${user?.referralCode || ''}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const totalClosedPnL = mt5Accounts.reduce((sum, acc) => sum + (acc.totalClosedPnL || 0), 0);
  const approvedAccountsCount = mt5Accounts.filter(a => a.status === 'approved').length;

  return (
    <div className="space-y-6">
      {/* 1. Header Welcome & Quick Action Banner */}
      <div className="bg-[#121215] p-5 sm:p-6 rounded-2xl border border-[#27272A] shadow-card flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-[#FFFFFF]">
              Welcome back, {user?.name}!
            </h2>
            <StatusBadge status={user?.status} label={user?.status} />
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1.5 flex flex-wrap items-center gap-2">
            <span>User ID: <strong className="font-mono text-[#F59E0B]">{user?.userIdTag}</strong></span>
            <span>•</span>
            <span>Referral Code: <strong className="font-mono text-[#F97316]">{user?.referralCode}</strong></span>
          </p>
        </div>

        <div className="flex items-center space-x-3 shrink-0">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => setActiveTab('mt5')}
            className="flex items-center space-x-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Connect MT5</span>
          </Button>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={() => setActiveTab('wallet')}
            className="flex items-center space-x-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Deposit USDT</span>
          </Button>
        </div>
      </div>

      {/* 2. Professional Getting Started Onboarding Step Flow */}
      <div className="bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#27272A] pb-3">
          <div>
            <span className="text-[10px] uppercase font-bold tracking-widest text-[#F97316]">Getting Started Guide</span>
            <h3 className="text-sm font-extrabold text-[#FFFFFF] mt-0.5">4-Step Platform Setup & Copy Trading Workflow</h3>
          </div>
          <span className="text-xs text-[#9CA3AF] font-medium">Follow these steps to activate automated profit sharing</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 relative">
          {/* Step 1 */}
          <div 
            onClick={() => setActiveTab('wallet')}
            className={`p-3.5 rounded-xl border transition-all cursor-pointer relative ${
              wallet?.balance > 0 
                ? 'bg-[#10B981]/10 border-[#10B981]/40 text-[#FFFFFF]' 
                : 'bg-[#09090B] border-[#F97316]/50 hover:border-[#F97316] text-[#FFFFFF]'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md ${
                wallet?.balance > 0 ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#F97316]/20 text-[#F97316]'
              }`}>
                {wallet?.balance > 0 ? '✓ Step 1 Done' : 'Step 1'}
              </span>
              <Wallet className={`w-4 h-4 ${wallet?.balance > 0 ? 'text-[#10B981]' : 'text-[#F97316]'}`} />
            </div>
            <h4 className="text-xs font-bold text-[#FFFFFF]">1. Deposit USDT</h4>
            <p className="text-[10px] text-[#9CA3AF] mt-1 leading-relaxed">
              Fund platform wallet with USDT to cover weekly profit share.
            </p>
          </div>

          {/* Step 2 */}
          <div 
            onClick={() => setActiveTab('mt5')}
            className={`p-3.5 rounded-xl border transition-all cursor-pointer relative ${
              mt5Accounts.length > 0 
                ? 'bg-[#10B981]/10 border-[#10B981]/40 text-[#FFFFFF]' 
                : wallet?.balance > 0
                  ? 'bg-[#09090B] border-[#F59E0B]/50 hover:border-[#F59E0B] text-[#FFFFFF]'
                  : 'bg-[#09090B] border-[#27272A] text-[#FFFFFF] opacity-80'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md ${
                mt5Accounts.length > 0 ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#F59E0B]/20 text-[#F59E0B]'
              }`}>
                {mt5Accounts.length > 0 ? '✓ Step 2 Done' : 'Step 2'}
              </span>
              <Cpu className={`w-4 h-4 ${mt5Accounts.length > 0 ? 'text-[#10B981]' : 'text-[#F59E0B]'}`} />
            </div>
            <h4 className="text-xs font-bold text-[#FFFFFF]">2. Connect MT5</h4>
            <p className="text-[10px] text-[#9CA3AF] mt-1 leading-relaxed">
              Submit MT5 Account ID, broker server & investor password.
            </p>
          </div>

          {/* Step 3 */}
          <div 
            className={`p-3.5 rounded-xl border transition-all relative ${
              approvedAccountsCount > 0 
                ? 'bg-[#10B981]/10 border-[#10B981]/40 text-[#FFFFFF]' 
                : 'bg-[#09090B] border-[#27272A] text-[#FFFFFF]'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md ${
                approvedAccountsCount > 0 ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#27272A] text-[#9CA3AF]'
              }`}>
                {approvedAccountsCount > 0 ? '✓ Step 3 Approved' : 'Step 3'}
              </span>
              <ShieldCheck className={`w-4 h-4 ${approvedAccountsCount > 0 ? 'text-[#10B981]' : 'text-[#9CA3AF]'}`} />
            </div>
            <h4 className="text-xs font-bold text-[#FFFFFF]">3. Admin Verification</h4>
            <p className="text-[10px] text-[#9CA3AF] mt-1 leading-relaxed">
              Admin approves account, locks initial balance, & connects copier.
            </p>
          </div>

          {/* Step 4 */}
          <div 
            className={`p-3.5 rounded-xl border transition-all relative ${
              approvedAccountsCount > 0 && totalClosedPnL > 0
                ? 'bg-[#10B981]/10 border-[#10B981]/40 text-[#FFFFFF]' 
                : 'bg-[#09090B] border-[#27272A] text-[#FFFFFF]'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md ${
                approvedAccountsCount > 0 ? 'bg-[#3B82F6]/20 text-[#3B82F6]' : 'bg-[#27272A] text-[#9CA3AF]'
              }`}>
                Step 4
              </span>
              <TrendingUp className={`w-4 h-4 ${approvedAccountsCount > 0 ? 'text-[#3B82F6]' : 'text-[#9CA3AF]'}`} />
            </div>
            <h4 className="text-xs font-bold text-[#FFFFFF]">4. Copy Trading & Profits</h4>
            <p className="text-[10px] text-[#9CA3AF] mt-1 leading-relaxed">
              Trades execute automatically. Profit sharing calculated weekly.
            </p>
          </div>
        </div>
      </div>

      {/* 2. Top 4 Metric Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <Card hoverEffect className="relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#9CA3AF]">Wallet Balance</span>
                <div className="w-8 h-8 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/20 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-[#F59E0B]" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-[#FFFFFF] mt-3">
                ${wallet?.balance ? wallet.balance.toFixed(2) : '0.00'}
              </p>
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span className="text-[#10B981] font-bold">USDT Active</span>
                <button 
                  onClick={() => setActiveTab('wallet')}
                  className="text-[#F97316] font-semibold hover:underline flex items-center space-x-0.5"
                >
                  <span>Deposit</span>
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </Card>

            <Card hoverEffect className="relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#9CA3AF]">MT5 Accounts</span>
                <div className="w-8 h-8 rounded-xl bg-[#F97316]/10 border border-[#F97316]/20 flex items-center justify-center">
                  <Cpu className="w-4 h-4 text-[#F97316]" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-[#FFFFFF] mt-3">
                {approvedAccountsCount} Active / {mt5Accounts.length} Total
              </p>
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span className="text-[#9CA3AF]">Copier Connected</span>
                <button 
                  onClick={() => setActiveTab('mt5')}
                  className="text-[#F97316] font-semibold hover:underline flex items-center space-x-0.5"
                >
                  <span>Manage</span>
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </Card>

            <Card hoverEffect className="relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#9CA3AF]">Total Trading Profit</span>
                <div className="w-8 h-8 rounded-xl bg-[#10B981]/10 border border-[#10B981]/20 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-[#10B981]" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-[#10B981] mt-3">
                +${totalClosedPnL.toFixed(2)}
              </p>
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span className="text-[#9CA3AF]">Closed PnL</span>
                <span className="text-[#10B981] font-semibold">Synced</span>
              </div>
            </Card>

            <Card hoverEffect className="relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#9CA3AF]">Referral Earnings</span>
                <div className="w-8 h-8 rounded-xl bg-[#3B82F6]/10 border border-[#3B82F6]/20 flex items-center justify-center">
                  <Users className="w-4 h-4 text-[#3B82F6]" />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-[#FFFFFF] mt-3">
                ${wallet?.totalReferralEarned ? wallet.totalReferralEarned.toFixed(2) : '0.00'}
              </p>
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span className="text-[#9CA3AF]">Referrals Active</span>
                <button 
                  onClick={() => setActiveTab('referrals')}
                  className="text-[#F97316] font-semibold hover:underline flex items-center space-x-0.5"
                >
                  <span>Tree</span>
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* 3. Structured 2-Column Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* LEFT COLUMN (2/3) - MT5 Accounts Summary & Recent Financial Activity */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* MT5 Connected Accounts Overview Table */}
          <Card 
            title="Connected MT5 Trading Accounts" 
            subtitle="Live status of connected MT5 trading accounts and closed PnL"
            headerAction={
              <Button variant="secondary" size="sm" onClick={() => setActiveTab('mt5')}>
                View All
              </Button>
            }
          >
            {loading ? (
              <SkeletonTable rows={2} cols={4} />
            ) : mt5Accounts.length === 0 ? (
              <div className="p-6 text-center space-y-3 bg-[#09090B] rounded-2xl border border-[#27272A]">
                <Cpu className="w-8 h-8 text-[#9CA3AF]/40 mx-auto" />
                <p className="text-xs text-[#9CA3AF]">No MT5 trading accounts connected yet.</p>
                <Button variant="primary" size="sm" onClick={() => setActiveTab('mt5')}>
                  Connect First MT5 Account
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                      <th className="py-2.5 px-3">Account</th>
                      <th className="py-2.5 px-3">Server</th>
                      <th className="py-2.5 px-3">Status</th>
                      <th className="py-2.5 px-3">Closed PnL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#27272A] text-xs">
                    {mt5Accounts.map((acc) => (
                      <tr key={acc._id} className="hover:bg-[#1A1A1E] transition-colors">
                        <td className="py-3 px-3 font-mono font-bold text-[#FFFFFF]">
                          #{acc.accountNumber}
                        </td>
                        <td className="py-3 px-3 text-[#9CA3AF]">
                          {acc.server}
                        </td>
                        <td className="py-3 px-3">
                          <StatusBadge status={acc.status} label={acc.status} />
                        </td>
                        <td className="py-3 px-3 font-bold text-[#10B981]">
                          +${acc.totalClosedPnL ? acc.totalClosedPnL.toFixed(2) : '0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Recent Financial Activity Feed */}
          <Card 
            title="Recent Wallet Ledger Activity" 
            subtitle="Latest deposits, profit share deductions, and referral payouts"
            headerAction={
              <Button variant="secondary" size="sm" onClick={() => setActiveTab('wallet')}>
                Full Audit Log
              </Button>
            }
          >
            {loading ? (
              <SkeletonTable rows={3} cols={4} />
            ) : transactions.length === 0 ? (
              <div className="p-6 text-center text-xs text-[#9CA3AF] bg-[#09090B] rounded-2xl border border-[#27272A]">
                No recent transaction history logged.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                      <th className="py-2.5 px-3">Type</th>
                      <th className="py-2.5 px-3">Amount</th>
                      <th className="py-2.5 px-3">Balance After</th>
                      <th className="py-2.5 px-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#27272A] text-xs">
                    {transactions.map((tx) => (
                      <tr key={tx._id} className="hover:bg-[#1A1A1E] transition-colors">
                        <td className="py-3 px-3">
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                            tx.type.includes('approval') || tx.type.includes('commission')
                              ? 'bg-[#10B981]/20 text-[#10B981]'
                              : tx.type.includes('deduction') || tx.type.includes('withdrawal')
                              ? 'bg-[#EF4444]/20 text-[#EF4444]'
                              : 'bg-[#27272A] text-[#9CA3AF]'
                          }`}>
                            {tx.type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className={`py-3 px-3 font-bold ${
                          tx.type.includes('deduction') || tx.type.includes('withdrawal') 
                            ? 'text-[#EF4444]' 
                            : 'text-[#10B981]'
                        }`}>
                          {tx.type.includes('deduction') || tx.type.includes('withdrawal') ? '-' : '+'}${tx.amount.toFixed(2)}
                        </td>
                        <td className="py-3 px-3 font-mono text-[#FFFFFF] font-bold">
                          ${tx.balanceAfter.toFixed(2)}
                        </td>
                        <td className="py-3 px-3 text-[#9CA3AF] text-[11px]">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT COLUMN (1/3) - Referral Widget & Quick Financial Tools */}
        <div className="space-y-6">
          
          <Card title="Referral Program Link" subtitle="Earn automated downline profit share">
            <div className="space-y-4">
              <div className="p-3 bg-[#09090B] rounded-xl border border-[#27272A] space-y-2">
                <span className="text-[10px] uppercase font-bold text-[#9CA3AF]">Your Personal Referral Link</span>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={refSummary?.referralLink || `${window.location.origin}/?ref=${user?.referralCode || ''}`}
                    className="w-full bg-[#121215] border border-[#27272A] text-[#F59E0B] font-mono text-xs rounded-lg px-2.5 py-1.5 focus:outline-none"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="p-2 rounded-lg bg-[#F97316] hover:bg-[#FB923C] text-white text-xs font-bold transition-colors shrink-0"
                    title="Copy Link"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {copied && <p className="text-[10px] text-[#10B981] font-semibold">✓ Referral link copied!</p>}
              </div>

              {/* Tier Rates Summary */}
              <div className="p-3.5 bg-[#09090B] rounded-xl border border-[#27272A] space-y-2">
                <span className="text-[10px] uppercase font-bold text-[#9CA3AF] block">Commission Tier Rates</span>
                <div className="flex items-center justify-around gap-1 text-center">
                  {Object.keys(refSummary?.rates || { level1: 4, level2: 3, level3: 2, level4: 1, level5: 2 }).map((lvlKey, idx) => (
                    <div key={lvlKey} className="flex-1 p-1.5 bg-[#121215] rounded-lg border border-[#27272A]">
                      <span className="text-[9px] text-[#9CA3AF] block">L{idx + 1}</span>
                      <span className="text-xs font-extrabold text-[#F97316]">
                        {refSummary?.rates ? refSummary.rates[lvlKey] : [4, 3, 2, 1, 2][idx]}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setActiveTab('referrals')}
                className="w-full flex items-center justify-center space-x-1.5"
              >
                <span>View Referral Tree</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </Card>

          {/* Quick Actions Shortcuts */}
          <Card title="Quick Platform Shortcuts">
            <div className="space-y-2.5">
              <button
                onClick={() => setActiveTab('wallet')}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-[#09090B] border border-[#27272A] hover:bg-[#1A1A1E] hover:border-[#F97316]/50 transition-all text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-[#10B981]/15 text-[#10B981]">
                    <ArrowDownRight className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#FFFFFF]">Deposit USDT</h4>
                    <p className="text-[10px] text-[#9CA3AF]">Fund internal wallet balance</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
              </button>

              <button
                onClick={() => setActiveTab('withdrawals')}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-[#09090B] border border-[#27272A] hover:bg-[#1A1A1E] hover:border-[#EF4444]/50 transition-all text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-[#EF4444]/15 text-[#EF4444]">
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#FFFFFF]">Request Withdrawal</h4>
                    <p className="text-[10px] text-[#9CA3AF]">Crypto USDT withdrawal</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
              </button>

              <button
                onClick={() => setActiveTab('mt5')}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-[#09090B] border border-[#27272A] hover:bg-[#1A1A1E] hover:border-[#F59E0B]/50 transition-all text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-[#F59E0B]/15 text-[#F59E0B]">
                    <Cpu className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#FFFFFF]">MT5 Account Portal</h4>
                    <p className="text-[10px] text-[#9CA3AF]">Submit or view credentials</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
              </button>
            </div>
          </Card>

        </div>

      </div>
    </div>
  );
};

export default UserDashboard;
